import React, { useEffect, useState } from 'react';
import { Layout, message, Button, Form, Input, Switch, Select, Tabs, Drawer, Space } from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import { useNavigate, useParams } from 'react-router-dom';
import MemberSidebar from './components/member.sidebar.component';
import MemberFooter from './components/member.footer.component';
import { useDispatch, useSelector } from 'react-redux';
import dot from 'dot-object';
import MediaSelect from './components/mediaSelect.field';
import EntityMedia from './entity.media.page';
import { API_CDN_URL } from '../services/config';
import {
    readArticle,
    createArticle,
    updateArticle,
    readAllActiveArticleTranslations
} from '../actions/entity.article'
import {
    readAllActiveArticleTagsTranslations
} from '../actions/entity.article.tag';
import {
    readAllActiveArticleCategoriesTranslations
} from '../actions/entity.article.category';
import { userIsReadOnlyInEntity } from '../helpers/user';
import { useTranslation } from 'react-i18next';
import SeoFields from './components/seo.component';
import Editor from './components/editor.field';
import moment from 'moment';

const layout = {
    labelCol: {
        span: 8,
    },
    wrapperCol: {
        span: 10,
    },
};

function Article() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const entities = useSelector(state => state.entity.entities);
    const currentEntity = entities ? entities.find(entity => entity.id === params.entityId) : null;
    const { Content } = Layout;
    const [form] = Form.useForm();
    const [entityArticleTitle, setArticleTitle] = useState('');
    const currentUser = useSelector(state => state.auth.user);
    const userIsReadOnly = userIsReadOnlyInEntity(currentUser, currentEntity);
    const requiredArticleSupportAllLanguages = currentEntity && currentEntity.requiredArticleSupportAllLanguages;
    const [addedLanguages, setAddedLanguages] = useState([]);
    const [mediaDrawerVisible, setMediaDrawerVisible] = useState(false);
    const [currentMediaFieldName, setCurrentMediaFieldName] = useState(null);
    const [articleData, setArticleData] = useState(null);
    const [tagOptions, setTagOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [articleOptions, setArticleOptions] = useState([]);

    const showMediaDrawer = (fieldName) => {
        setCurrentMediaFieldName(fieldName);
        setMediaDrawerVisible(true);
    };
    const onMediaDrawerClose = () => {
        setMediaDrawerVisible(false);
    };

    const onSelectMedia = (media) => {
        setMediaDrawerVisible(false);
        if (currentMediaFieldName && currentEntity) {
            let url = API_CDN_URL(currentEntity) + media.path;
            let newValue = {
                [currentMediaFieldName.join('.')]: url
            };
            let formValues = form.getFieldsValue();
            // Use dot to update partial values
            newValue = dot.dot(newValue);
            formValues = dot.dot(formValues);
            newValue = { ...formValues, ...newValue };
            newValue = dot.object(newValue);
            form.setFieldsValue(newValue);
        }
    }

    useEffect(() => {
        if (currentEntity) {
            if (currentEntity.requiredArticleSupportAllLanguages) {
                setAddedLanguages(currentEntity.languages.filter(language => {
                    return language.status
                }).map(language => {
                    return {
                        label: language.name,
                        value: language.code
                    }
                }));
            } else {
                if (articleData && typeof articleData.languages !== 'undefined' && Array.isArray(articleData.languages)) {
                    setAddedLanguages(currentEntity.languages.filter(language => {
                        return language.status && articleData.languages.includes(language.code)
                    }).map(language => {
                        return {
                            label: language.name,
                            value: language.code
                        }
                    }));
                }
            }
            // Fetch the categories
            dispatch(readAllActiveArticleCategoriesTranslations(currentEntity.id)).then((response) => {
                if (typeof response.result !== 'undefined') {
                    setCategoryOptions(response.result.map(categoryTranslation => {
                        return {
                            label: categoryTranslation.name,
                            value: categoryTranslation.id,
                            language: categoryTranslation.language
                        }
                    }));
                }
            }).catch(error => {
                console.log(error);
                message.error(error.message);
            });

            // Fetch the tags
            dispatch(readAllActiveArticleTagsTranslations(currentEntity.id)).then((response) => {
                if (typeof response.result !== 'undefined') {
                    setTagOptions(response.result.map(tagTranslation => {
                        return {
                            label: tagTranslation.name,
                            value: tagTranslation.id,
                            language: tagTranslation.language
                        }
                    }));
                }
            }).catch(error => {
                console.log(error);
                message.error(error.message);
            });

            // Fetch the article
            dispatch(readAllActiveArticleTranslations(currentEntity.id)).then((response) => {
                if (typeof response.result !== 'undefined') {
                    setArticleOptions(response.result.filter(articleTranslation => {
                        console.log(articleTranslation);
                        if (typeof params.articleId !== 'undefined') {
                            return articleTranslation.parent._id !== params.articleId;
                        } else {
                            return true;
                        }
                    }).map(articleTranslation => {
                        return {
                            label: articleTranslation.title,
                            value: articleTranslation.id,
                            language: articleTranslation.language
                        }
                    }));
                }
            }).catch(error => {
                console.log(error);
                message.error(error.message);
            });
        }
    }, [currentEntity, articleData]);
    const languageOptions = !requiredArticleSupportAllLanguages && currentEntity && currentEntity.languages ? currentEntity.languages.map(language => {
        return {
            label: language.name,
            value: language.code,
            disabled: !language.status
        }
    }) : [];

    const onFinish = (values) => {
        if (userIsReadOnly) {
            message.error(t('entityArticle.message.read-only'));
            return;
        }
        if (typeof params.articleId !== 'undefined') {
            // Update the existing entity article
            dispatch(updateArticle(params.entityId, params.articleId, values)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setArticleTitle(t('entityArticle.title.edit'));
                    if (typeof response.result.date !== 'undefined') {
                        response.result.date = moment(response.result.date).format('YYYY-MM-DD');
                    }
                    form.setFieldsValue(response.result);
                    setArticleData(response.result);
                }
                message.success(t('entityArticle.message.update-success'));
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        } else {
            // Create new campaign
            dispatch(createArticle(params.entityId, values)).then((data) => {
                message.success(t('entityArticle.message.create-success'));
                if (data.result) {
                    navigate(`/entity/${params.entityId}/article/${data.result.id}`);
                } else {
                    navigate(`/entity/${params.entityId}/articles`);
                }
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        }
    }

    useEffect(() => {
        if (typeof params.articleId !== 'undefined') {
            setArticleTitle(t('entityArticle.title.edit'));
            dispatch(readArticle(params.entityId, params.articleId)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setArticleTitle(t('entityArticle.title.edit'));
                    // convert the date
                    if (typeof response.result.date !== 'undefined') {
                        response.result.date = moment(response.result.date).format('YYYY-MM-DD');
                    }
                    form.setFieldsValue(response.result);
                    setArticleData(response.result);
                } else {
                    message.error(t('entityArticle.message.error-reading'));
                    navigate(`/entity/${params.entityId}/articles`);
                }
            }).catch(e => {
                message.error(t('entityArticle.message.error-reading'));
                navigate(`/entity/${params.entityId}/articles`);
            });
        } else {
            setArticleTitle(t('entityArticle.title.new'));
        }
    }, [params.articleId, params.entityId, dispatch, navigate, form]);

    const onFinishFailed = (errorInfo) => {
        if (typeof errorInfo.errorFields !== 'undefined') {
            errorInfo.errorFields.forEach(field => {
                message.error(field.errors[0]);
            });
        }
    }

    const selectedLanguages = (options) => {
        if (Array.isArray(options)) {
            const filteredLanguages = languageOptions.filter(language => {
                if (options.includes(language.value)) {
                    return true;
                } else {
                    return false;
                }
            });
            console.log(options);
            console.log(filteredLanguages);
            setAddedLanguages(filteredLanguages);
        } else {
            setAddedLanguages([]);
        }
    }

    let extraButtons = [
        <Button key="cancel" type="primary" danger href={`/entity/${params.entityId}/articles`}>{t('entityArticle.actions.cancel')}</Button>
    ];
    if (!userIsReadOnly) {
        extraButtons.push(<Button key="save" type="primary" htmlType="submit">{params.articleId ? t('entityArticle.actions.update') : t('entityArticle.actions.save')}</Button>);
    }
    return <>
        <MemberSidebar />
        <Layout className="site-layout">
            <Content style={{ margin: '0 16px' }}>
                <div className="site-layout-background">
                    <Form
                        {...layout}
                        form={form}
                        name="entity-article"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        initialValues={{
                            status: true
                        }}>
                        <PageHeader
                            className="site-page-header"
                            onBack={() => navigate(`/entity/${params.entityId}/articles`)}
                            title={entityArticleTitle}
                            extra={extraButtons}
                        />
                        <>
                            <Form.Item
                                label={t('entityArticle.field.status.label')}
                                name="status"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                label={t('entityArticle.field.slug.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="slug"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('entityArticle.field.slug.required')
                                        },
                                        { pattern: /^[a-z0-9-_]+$/, message: t('entityArticle.field.slug.pattern') }
                                    ]}
                                    noStyle>
                                    <Input placeholder={t('entityArticle.field.slug.placeholder')} />
                                </Form.Item>
                                <div>{t('entityArticle.field.slug.note')}</div>
                            </Form.Item>
                            <Form.Item
                                label={t('entityArticle.field.date.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="date"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('entityArticle.field.date.required')
                                        }
                                    ]}
                                    noStyle>
                                    <Input placeholder={t('entityArticle.field.date.placeholder')} />
                                </Form.Item>
                                <div>{t('entityArticle.field.date.note')}</div>
                            </Form.Item>
                            {!requiredArticleSupportAllLanguages && <Form.Item
                                label={t('entityArticle.field.languages.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="languages"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('entityArticle.field.languages.required')
                                        }
                                    ]}
                                    noStyle>
                                    <Select mode="multiple" allowClear options={languageOptions} onChange={selectedLanguages} />
                                </Form.Item>
                                <div>{t('entityArticle.field.languages.note')}</div>
                            </Form.Item>}
                            <Tabs>
                                {addedLanguages.map((language, index) => {
                                    return <Tabs.TabPane tab={language.label} key={language.value} forceRender>
                                        <Form.Item
                                            label={t('entityArticle.field.title.label')}
                                            name={[language.value, 'title']}
                                            required={true}
                                            rules={
                                                [
                                                    {
                                                        required: true,
                                                        message: t('entityArticle.field.title.required')

                                                    }
                                                ]}
                                        >
                                            <Input placeholder={t('entityArticle.field.title.placeholder')} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('entityArticle.field.subtitle.label')}
                                            name={[language.value, 'subtitle']}
                                        >
                                            <Input placeholder={t('entityArticle.field.subtitle.placeholder')} />
                                        </Form.Item>
                                        <MediaSelect showMediaDrawer={showMediaDrawer} form={form} wrapper={{
                                            wrapperCol: 16,
                                            label: t('entityArticle.field.banner.label')
                                        }} inner={{
                                            name: [language.value, 'banner'],
                                        }} />
                                        <Form.Item
                                            label={t('entityArticle.field.tags.label')}
                                            name={[language.value, 'tags']}
                                        >
                                            <Select mode="multiple" allowClear options={tagOptions.filter(tagTranslation => {
                                                if (tagTranslation.language === language.value) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            })} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('entityArticle.field.categories.label')}
                                            name={[language.value, 'categories']}
                                        >
                                            <Select mode="multiple" allowClear options={categoryOptions.filter(categoryTranslation => {
                                                if (categoryTranslation.language === language.value) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            })} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('entityArticle.field.relatedArticles.label')}
                                            name={[language.value, 'relatedArticles']}
                                        >
                                            <Select mode="multiple" allowClear options={articleOptions.filter(articleOptions => {
                                                if (articleOptions.language === language.value) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            })} />
                                        </Form.Item>
                                        {/* <Form.Item
                                            labelCol={{ span: 0 }}
                                            wrapperCol={{ span: 24 }}
                                        >
                                            <h1>{t('entityArticle.field.content.label')}</h1>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                                <div>{t('entityArticle.field.content.instruction')}</div>
                                                <div><Button key="open-editor" type="primary" onClick={() => initializeEditor([language.value, 'content'])}>{t('entityPage.actions.open-editor')}</Button></div>
                                            </div>
                                            <Form.Item
                                                noStyle
                                                name={[language.value, 'content']}>
                                                <Input.TextArea autoSize={{ minRows: 10, maxRows: 20 }} />
                                            </Form.Item>
                                        </Form.Item> */}
                                        <Editor
                                            label={t('entityArticle.field.content.label')}
                                            language={language.value}
                                            name={[language.value, 'content']}
                                            showMediaDrawer={showMediaDrawer}
                                            form={form}
                                        />
                                        <SeoFields
                                            name={[language.value]}
                                            layout={layout}
                                            form={form}
                                            showMediaDrawer={showMediaDrawer}
                                        />
                                    </Tabs.TabPane>
                                })}
                            </Tabs>
                        </>
                    </Form>
                    <Drawer contentWrapperStyle={{ width: '1000px', maxWidth: '100%', minWidth: '50%' }} title={t('mediaDrawer.title')} placement="right" onClose={onMediaDrawerClose} open={mediaDrawerVisible}>
                        <EntityMedia entityId={params.entityId} selectMedia={onSelectMedia} />
                    </Drawer>
                </div>
            </Content>
            <MemberFooter />
        </Layout>
    </>;
}

export default Article;